import React, { useEffect, useState } from "react";
import { api, API } from "../../lib/api";
import { RotateCcw, Download } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";

const inp = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900";

export default function Transactions() {
  const [list, setList] = useState([]);
  const [status, setStatus] = useState("");
  const [adjust, setAdjust] = useState(null);

  const load = () => {
    const q = status ? `?status=${status}` : "";
    api.get(`/admin/transactions${q}`).then((r) => setList(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [status]);

  const doAdjust = async () => {
    try {
      await api.post(`/admin/transactions/${adjust.transaction_ref}/adjust`, { type: adjust.type, reason: adjust.reason });
      toast.success("Adjustment recorded"); setAdjust(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-3xl font-bold">Transaction Ledger</h1>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="txn-status-filter" className="rounded-lg border border-white/10 bg-[#1C2541] px-3 py-2 text-sm">
          <option value="">All statuses</option><option value="completed">Completed</option><option value="pending">Pending</option><option value="failed">Failed</option>
        </select>
      </div>
      <p className="mt-1 text-sm text-emerald-100/50">Completed financial records are preserved. Corrections use void / refund / reversal / correction — never deletion.</p>

      <div className="mt-6 overflow-x-auto rounded-2xl border border-white/10 bg-[#1C2541]">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-white/10 text-left text-xs uppercase text-emerald-100/40">
            <th className="p-3">Reference</th><th className="p-3">Receipt</th><th className="p-3">Provider</th><th className="p-3">Program</th><th className="p-3">Amount</th><th className="p-3">Fee</th><th className="p-3">Status</th><th className="p-3">Refund</th><th className="p-3"></th>
          </tr></thead>
          <tbody>
            {list.map((t) => (
              <tr key={t.id} className="border-b border-white/5" data-testid={`txn-row-${t.transaction_ref}`}>
                <td className="p-3 font-mono text-xs">{t.transaction_ref}{t.test_mode && <span className="ms-1 rounded bg-gold/20 px-1 text-[9px] text-gold-light">TEST</span>}</td>
                <td className="p-3 font-mono text-xs">{t.receipt_number || "—"}</td>
                <td className="p-3 text-emerald-100/60">{t.provider_name}</td>
                <td className="p-3">{t.campaign_title}</td>
                <td className="p-3">{t.currency} {t.amount?.toLocaleString()}</td>
                <td className="p-3 text-emerald-100/50">{t.gateway_fee?.toFixed(2)}</td>
                <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-xs ${t.status === "completed" ? "bg-emerald-800" : t.status === "failed" ? "bg-red-900" : "bg-amber-900"}`}>{t.status}</span></td>
                <td className="p-3 text-emerald-100/50">{t.refund_status}</td>
                <td className="p-3">
                  <div className="flex gap-1.5">
                    {t.receipt_number && <a href={`${API}/receipts/${t.receipt_number}/pdf`} target="_blank" rel="noreferrer" className="rounded-lg bg-white/5 p-2 hover:bg-white/10" title="Receipt PDF"><Download className="h-3.5 w-3.5" /></a>}
                    {t.status === "completed" && t.refund_status === "none" && <button onClick={() => setAdjust({ transaction_ref: t.transaction_ref, type: "refund", reason: "" })} data-testid={`adjust-${t.transaction_ref}`} className="rounded-lg bg-white/5 p-2 hover:bg-white/10" title="Adjust"><RotateCcw className="h-3.5 w-3.5" /></button>}
                  </div>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan="9" className="p-8 text-center text-emerald-100/40">No transactions yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!adjust} onOpenChange={(v) => !v && setAdjust(null)}>
        <DialogContent className="bg-white sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl text-emerald-900">Financial Adjustment</DialogTitle></DialogHeader>
          {adjust && (
            <div className="space-y-3 text-slate-900">
              <p className="text-sm text-slate-500">Original transaction is preserved. This creates an adjustment record and an audit log.</p>
              <label className="block text-sm">Type<select className={inp} value={adjust.type} onChange={(e) => setAdjust({ ...adjust, type: e.target.value })} data-testid="adjust-type"><option value="refund">Refund</option><option value="void">Void</option><option value="reversal">Reversal</option><option value="correction">Correction</option></select></label>
              <label className="block text-sm">Reason<textarea rows={3} className={inp} value={adjust.reason} onChange={(e) => setAdjust({ ...adjust, reason: e.target.value })} /></label>
              <button onClick={doAdjust} data-testid="confirm-adjust" className="w-full rounded-full bg-emerald-900 py-3 text-sm font-bold text-white hover:bg-emerald-800">Record Adjustment</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
